import { AdAccountDTO } from 'api-models'

import {
	GLazyLoading
} from 'components/basic-blocks'
import { PageLayout } from 'components/page-layout'
import { AnnouncementSpotlight } from 'components/refunds-agent'

import moment from 'moment'
import { lazy, Suspense, useEffect, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import {
	useAccountStore,
	useAdminStore,
	useAssetStore,
	useUiStore,
	useUserStore
} from 'store'
import { PageTitle } from 'utils/page-title'
import { useShallow } from 'zustand/react/shallow'
import { useAsset } from '../asset-base'
import { GDateRange } from '../../../../components/basic-blocks/g-date-range'
import { Calendar } from 'components/icons'
import { useFilterPersistence } from 'hooks/use-filter-presistence'
import { Notices } from 'components/notice/notices'
import { useBreadcrumbSelection } from 'components/breadcrumb-bar'
import { useWorkspaceAccess } from 'hooks/use-workspace-access'
import { NoAdAccountDashboard } from './components/no-ad-account-dashboard'
import { NotStartedDashboard } from './components/not-started-dashboard'

const Dashboard = lazy(() =>
	import('./view')
		.then((module) => {
			return { default: module.Dashboard }
		})
		.catch((error) => {
			console.error('Failed to load:', error)
			return { default: () => <div>Error loading the component.....</div> }
		})
)

export const DashboardPage = () => {
	const { impersonation } = useAdminStore(
		useShallow((s) => ({
			impersonation: s.impersonation
		}))
	)
	const isLoading = useUiStore((s) => s.isLoading)
	const { access, ready: accessReady } = useWorkspaceAccess()
	const noAdAccount = access === 'no-ad-account' || access === 'no-setup'
	const notStarted = access === 'not-started'
	const { accountId, assetId } = useAsset()
	const { asset, adAccounts, refreshAdAccounts, demoMode } = useAssetStore(
		useShallow((s) => ({
			asset: s.asset,
			adAccounts: s.adAccounts,
			refreshAdAccounts: s.refreshAdAccounts,
			demoMode: s.demoMode
		}))
	)
	const { isInSetup, account, hasFeature } = useAccountStore(
		useShallow((s) => ({
			isInSetup: s.isInSetup,
			account: s.account,
			hasFeature: s.hasFeature
		}))
	)
	const { user } = useUserStore(
		useShallow((s) => ({
			user: s.user
		}))
	)
	const [searchParams, setSearchParams] = useSearchParams()
	const { adAccountIdsCsv, campaignIdsCsv } = useBreadcrumbSelection()
	const breadPlatforms = searchParams.get('platforms')
	const [adAccount, setAdAccount] = useState<AdAccountDTO[]>([])
const [dateRange, setDateRange] = useState({
	from: moment().subtract(30, 'days').startOf('day').toDate(),
	to: moment().startOf('hour').toDate()
})
const hasInitializedDateRange = useRef(false)
const isMountedRef = useRef(true)
const dateLoadingTimeoutRef = useRef<NodeJS.Timeout | null>(null)
	
	const [dateLoading, setDateLoadingState] = useState(false)
	const hasInitializedFromURL = useRef(false)
	
	useEffect(() => {
		isMountedRef.current = true
		return () => {
			isMountedRef.current = false
			if (dateLoadingTimeoutRef.current) {
				clearTimeout(dateLoadingTimeoutRef.current)
			}
		}
	}, [])
	
	useEffect(() => {
		if (!isMountedRef.current) return
		
		const fromParam = searchParams.get('from')
		const toParam = searchParams.get('to')
		
		if (fromParam && toParam) {
			const newFrom = moment(Number(fromParam)).toDate()
			const newTo = moment(Number(toParam)).toDate()
			
			if (
				!dateRange.from ||
				!dateRange.to ||
				dateRange.from.getTime() !== newFrom.getTime() ||
				dateRange.to.getTime() !== newTo.getTime()
			) {
				if (isMountedRef.current) {
					setDateRange({
						from: newFrom,
						to: newTo
					})
				}
			}
		} else if (!hasInitializedDateRange.current) {
			if (isMountedRef.current) {
				setDateRange({
					from: moment().subtract(30, 'days').startOf('day').toDate(),
					to: moment().startOf('hour').toDate()
				})
				hasInitializedDateRange.current = true
			}
		}
		
		if (fromParam && toParam) {
			hasInitializedDateRange.current = true
		}
	}, [searchParams])
	
	useFilterPersistence()
	useEffect(() => {
		refreshAdAccounts()
	}, [asset, impersonation])
	
	useEffect(() => {
		if (hasInitializedFromURL.current || adAccounts.length === 0) {
			return
		}
		
		const preSelectedAdAccount = searchParams.get('adAccounts') || searchParams.get('adAccount')
		let matchedAccounts: AdAccountDTO[] = []
		if (preSelectedAdAccount) {
			const ids = preSelectedAdAccount.split(',')
			matchedAccounts = adAccounts.filter((acc) => ids.includes(acc.id))
			if (breadPlatforms) {
				matchedAccounts = matchedAccounts.filter((acc) =>
					breadPlatforms.includes(acc.platform)
				)
			}
		} else {
			if (breadPlatforms) {
				matchedAccounts = adAccounts.filter((acc) =>
					breadPlatforms.includes(acc.platform)
				)
			}
		}
		setAdAccount(matchedAccounts)
		const syncedIds = matchedAccounts
			.map((a) => a?.id)
			.sort()
			.join(',')
		lastSyncedAdAccountIdsRef.current = syncedIds || ''
		hasInitializedFromURL.current = true
	}, [adAccounts, breadPlatforms]) // Removed searchParams - only initialize once when adAccounts load

	const prevAdAccountRef = useRef<string>(
		adAccount
			.map((a) => a?.id)
			.sort()
			.join(',')
	)
	const lastSyncedAdAccountIdsRef = useRef<string | null>(null)
	
	useEffect(() => {
		if (!hasInitializedFromURL.current) {
			return
		}

		const currentAdAccountIds = adAccount
			.map((a) => a?.id)
			.sort()
			.join(',')
		const adAccountChanged = prevAdAccountRef.current !== currentAdAccountIds
		
		
		if (!adAccountChanged && lastSyncedAdAccountIdsRef.current === currentAdAccountIds) {
			return
		}
		
		prevAdAccountRef.current = currentAdAccountIds
		
		const currentUrlParams = new URLSearchParams(window.location.search)
		const currentUrlIds = currentUrlParams.get('adAccounts')
		const expectedIds = adAccount.length > 0 ? adAccount.map((a) => a?.id).join(',') : null
		
		if (adAccount.length === 0) {
			if (!currentUrlIds && !currentUrlParams.get('adAccount')) {
				lastSyncedAdAccountIdsRef.current = ''
				return 
			}
		} else {
			if (currentUrlIds === expectedIds) {
				lastSyncedAdAccountIdsRef.current = currentAdAccountIds
				return // Already matches
			}
		}
		
		
		const newParams = new URLSearchParams(window.location.search)

		if (newParams.get('adAccount') && !newParams.get('adAccounts')) {
			const singularId = newParams.get('adAccount')
			if (
				adAccount.length > 0 &&
				adAccount.some((acc) => acc.id === singularId)
			) {
				newParams.delete('adAccount')
			}
		}
		
			
		if (adAccount.length === 0) {
			if (newParams.get('adAccounts') || newParams.get('adAccount')) {
				newParams.delete('adAccounts')
				newParams.delete('adAccount')
			} else {
				lastSyncedAdAccountIdsRef.current = ''
				return 
			}
		} else {
			const ids = adAccount.map((a) => a?.id).join(',')
			if (currentUrlIds !== ids) {
				newParams.set('adAccounts', ids)
				newParams.delete('adAccount')
			} else {
				lastSyncedAdAccountIdsRef.current = currentAdAccountIds
				return // Already matches, no update needed
			}
		}
		
		if (newParams.toString() !== window.location.search.substring(1)) {
			setSearchParams(newParams)
			lastSyncedAdAccountIdsRef.current = currentAdAccountIds
		} else {
			lastSyncedAdAccountIdsRef.current = currentAdAccountIds
		}
	}, [adAccount, setSearchParams]) 

	const handleDateRangeChange = (e: { from: Date; to: Date }) => {
		if (!isMountedRef.current) return
		
		setDateRange({ from: e.from, to: e.to })
		setDateLoadingState(true)
		
		if (dateLoadingTimeoutRef.current) {
			clearTimeout(dateLoadingTimeoutRef.current)
		}
		
		dateLoadingTimeoutRef.current = setTimeout(() => {
			if (isMountedRef.current) {
				setDateLoadingState(false)
			}
		}, 1500)
	}
	// A real workspace with no website yet shows the demo website; name the
	// workspace in the header and tab title instead of "demo.clickguard.com".
	const subjectName =
		asset?.id === 'website-demo' && account?.name ? account.name : asset?.name
	const shouldTheComponentLoad =
		isLoading('ASSET_GET_ACCOUNTS') ||
		isLoading('ACCOUNT_GET_ASSET') ||
		isLoading('USER_GET_ACCOUNTS') ||
		dateLoading

	const isReady = assetId && dateRange.from && dateRange.to

	return (
		<>
			<PageTitle pageName="Dashboard" entityName={subjectName} />
			<PageLayout
				title="Dashboard"
				// Case 2 reads as a summary of what protection will report; every
				// other case keeps "Summary metrics for <website>".
				subtitle={notStarted ? 'Invalid traffic protection summary' : undefined}
				tagline={notStarted ? undefined : 'Summary metrics for'}
				tags={notStarted ? undefined : [{ label: subjectName }]}
				topAction={undefined}
				datePicker={
					noAdAccount || notStarted ? undefined : (
						<GDateRange
							onApply={(e) => handleDateRangeChange({ from: e.from, to: e.to })}
							align="right"
							loading={shouldTheComponentLoad}
							demoMode={demoMode}
							calender={Calendar}
						/>
					)
				}
				notices={
					<Notices
						isInSetup={isInSetup()}
						setupStep={account?.setup?.step}
						isInActive={
							account?.status === 'INACTIVE' || access === 'cancelled'
						}
						hasDashboardPerformanceFeature={hasFeature('dashboard_performance')}
						tc_installed={!!asset?.tc_installed}
						cc_installed={!!asset?.cc_installed}
						tc_installed_skipped={!!asset?.tc_installed_skipped}
						accountId={accountId}
						assetId={assetId}
						demoMode={demoMode}
						mccLinkInviteAdAccounts={adAccounts}
						hideDemoDataNotice={notStarted}
					/>
				}
			>
				<AnnouncementSpotlight />
				{!accessReady ? (
					<GLazyLoading />
				) : notStarted ? (
					<NotStartedDashboard
						accountId={account?.id}
						accountName={account?.name}
					/>
				) : noAdAccount ? (
					<NoAdAccountDashboard />
				) : (
					<Suspense fallback={<GLazyLoading />}>
						<Dashboard
							asset={asset}
							accountId={accountId}
							assetId={assetId}
							from={dateRange.from}
							to={dateRange.to}
							adAccountId={
								adAccountIdsCsv || adAccount.map((item) => item?.id).join(',')
							}
							platform={breadPlatforms || undefined}
							campaignId={campaignIdsCsv || undefined}
							isReady={!!isReady}
							demoMode={demoMode}
						/>
					</Suspense>
				)}
			</PageLayout>
		</>
	)
}
